import type { Cents } from "../types/money";
import { VAT_RATE } from "../types/money";
import type { CustomerDetails, Order, OrderItem } from "../types/order";
import { renderInvoiceHtml, type InvoiceRenderOptions } from "../invoice";

/**
 * Een factuur die niet uit een webshop-bestelling komt: montagewerk, een losse levering, een
 * nabetaling. Tot nu toe werden die buiten het systeem om gemaakt, met het risico dat de gegevens
 * afwijken van wat er in de admin staat.
 *
 * **Eén sjabloon.** De factuur wordt niet opnieuw getekend: `factuurAlsOrder()` zet hem om naar
 * de vorm die `renderInvoiceHtml` al kent. Daardoor ziet een losse factuur er per definitie
 * hetzelfde uit als die van een bestelling — in huisstijl, met dezelfde btw-opbouw en dezelfde
 * betaalinstructie — en levert de voorvertoning in de admin exact de factuur op die de klant
 * krijgt. Verandert de huisstijl, dan verandert alles mee.
 *
 * **Prijzen zijn vrij.** Elke regel heeft een eigen aantal, stukprijs (excl. btw) en btw-tarief:
 * niets wordt uit de catalogus gehaald. Dat is het verschil met een bestelling, waar de server
 * juist de autoriteit is over de prijs.
 */

/** Btw-tarief per regel. 0 = vrijgesteld of verlegd; de reden staat op de factuur zelf. */
export type FactuurBtw = 0 | 9 | 21;

export interface FactuurRegel {
  /** Wat er geleverd is. Verplicht — een factuurregel zonder omschrijving is niet te controleren. */
  omschrijving: string;
  aantal: number;
  /** Stukprijs excl. btw, in centen. Mag negatief zijn voor een korting-regel. */
  stukprijsExclCents: Cents;
  btwTarief: FactuurBtw;
  /**
   * Ons artikelnummer, als de regel uit de webshop is overgenomen. Komt onder de omschrijving op
   * de factuur te staan, net als bij een bestelling — zonder dat nummer kan de klant een regel
   * niet terugvinden in de shop en wij niet in de voorraad. (Taco, 05-11-2026.)
   */
  artikelnummer?: string;
  /**
   * De webshop-slug van het artikel, als de regel uit de shop is overgenomen. Niet voor de klant:
   * dit is de sleutel waarmee de server de INKOOPprijs kan opzoeken als deze factuur een
   * bestelling wordt. Zonder die sleutel heet de orderregel `regel-1` en kan de bestelbon naar
   * Timmers geen prijs vinden — dan staat er een streepje. (Taco, 05-11-2026.)
   */
  slug?: string;
}

export type FactuurStatus = "concept" | "definitief" | "verstuurd";

export interface Factuur {
  id: string;
  /** `F-2026-001`. Pas toegekend bij definitief maken; een concept heeft nog geen nummer. */
  nummer?: string;
  status: FactuurStatus;
  /** Factuurdatum (epoch ms). Bij definitief maken vastgezet. */
  datum: number;
  /** Betaaltermijn in dagen; alleen voor de tekst op de factuur. */
  betaaltermijnDagen?: number;
  klant: CustomerDetails;
  regels: FactuurRegel[];
  /** Vrije verwijzing van de klant (projectnummer, inkoopordernummer). */
  referentie?: string;
  /** Btw verlegd naar de afnemer (intracommunautair). Alle regels tellen dan als 0%. */
  btwVerlegd?: boolean;
  /** Bestelling waar deze factuur bij hoort, als die er is. */
  orderId?: string;
  aangemaaktOp: number;
  aangemaaktDoor?: string;
  definitiefOp?: number;
  verstuurdOp?: number;
  verstuurdNaar?: string;
  /**
   * Betaling via een Mollie-betaallink bij de conceptfactuur.
   *
   * De route die Taco beschreef (27-10-2026): een concept gaat met een betaallink naar de klant,
   * en pas als die betaald is ontstaat de echte factuur mét nummer. `betaaldOp` is dat moment —
   * en tegelijk het enige punt waarop deze factuur "Voldaan" mag tonen.
   */
  molliePaymentId?: string;
  /** De betaallink zelf, zodat hij opnieuw te sturen is zonder een tweede betaling te maken. */
  checkoutUrl?: string;
  /** Wanneer het concept mét betaallink is verstuurd (epoch ms). */
  betaallinkVerstuurdOp?: number;
  /** Wanneer Mollie "betaald" meldde (epoch ms). Leeg = nog niet voldaan. */
  betaaldOp?: number;
}

export interface FactuurTotalen {
  subtotaal: Cents;
  btw: Cents;
  totaal: Cents;
  /** Btw per tarief — hoort op een factuur met meerdere tarieven. */
  perTarief: { tarief: FactuurBtw; grondslag: Cents; btw: Cents }[];
}

/** Regeltotaal excl. btw. */
export function regelTotaalExcl(r: FactuurRegel): Cents {
  return Math.round(r.stukprijsExclCents * r.aantal);
}

/**
 * Telt de factuur op. Btw wordt **per tarief** berekend en pas daarna afgerond, net als op een
 * gewone factuur: eerst alle 21%-regels bij elkaar, dan één keer afronden. Per regel afronden
 * geeft centen verschil met de boekhouding.
 */
export function factuurTotalen(f: Pick<Factuur, "regels" | "btwVerlegd">): FactuurTotalen {
  const verlegd = f.btwVerlegd === true;
  const perTariefMap = new Map<FactuurBtw, Cents>();
  let subtotaal = 0;
  for (const r of f.regels) {
    const excl = regelTotaalExcl(r);
    subtotaal += excl;
    const tarief: FactuurBtw = verlegd ? 0 : r.btwTarief;
    perTariefMap.set(tarief, (perTariefMap.get(tarief) ?? 0) + excl);
  }
  const perTarief = [...perTariefMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([tarief, grondslag]) => ({ tarief, grondslag, btw: Math.round((grondslag * tarief) / 100) }));
  const btw = perTarief.reduce((s, x) => s + x.btw, 0);
  return { subtotaal, btw, totaal: subtotaal + btw, perTarief };
}

/** Het eerstvolgende factuurnummer: `F-2026-001`. */
export function factuurNummer(jaar: number, volgnummer: number): string {
  return `F-${jaar}${volgnummer}`;
}

/**
 * Wat er nog ontbreekt om deze factuur definitief te mogen maken. Lege lijst = klaar.
 *
 * Dit is de fiscale checklist: een factuur zonder nummer, datum, afnemer met adres, of zonder
 * controleerbare regels is niet geldig. Server én scherm gebruiken dezelfde functie, zodat het
 * scherm nooit iets toelaat wat de server daarna weigert.
 */
export function factuurOntbreekt(f: Pick<Factuur, "klant" | "regels" | "btwVerlegd">): string[] {
  const uit: string[] = [];
  const k = f.klant;
  const a = k?.billingAddress ?? k?.address;
  if (!`${k?.firstName ?? ""} ${k?.lastName ?? ""}`.trim() && !k?.companyName?.trim()) uit.push("naam of bedrijfsnaam van de afnemer");
  if (!a?.street?.trim() || !a?.postalCode?.trim() || !a?.city?.trim()) uit.push("adres van de afnemer");
  if (!f.regels.length) uit.push("minstens één factuurregel");
  f.regels.forEach((r, i) => {
    if (!r.omschrijving.trim()) uit.push(`omschrijving bij regel ${i + 1}`);
    if (!(r.aantal > 0)) uit.push(`aantal bij regel ${i + 1}`);
  });
  // Btw verleggen kan alleen met het btw-nummer van de afnemer erop; zonder dat nummer is de
  // factuur formeel niet in orde en kan de Belastingdienst de nultarief-claim afwijzen.
  if (f.btwVerlegd && !k?.vatNumber?.trim()) uit.push("btw-nummer van de afnemer (nodig bij verlegde btw)");
  if (factuurTotalen(f).totaal === 0) uit.push("een bedrag (het totaal is nu € 0,00)");
  return uit;
}

/**
 * Zet een factuur om naar de ordervorm die `renderInvoiceHtml` tekent.
 *
 * Bewust géén tweede sjabloon: dat zou langzaam uit de pas lopen met de factuur die klanten bij
 * een bestelling krijgen. De regels worden gewone orderregels, de totalen komen uit
 * `factuurTotalen`, en de verlegging wordt op dezelfde manier doorgegeven als bij een order.
 */
export function factuurAlsOrder(f: Factuur): Order {
  const tot = factuurTotalen(f);
  const items: OrderItem[] = f.regels.map((r, i) => ({
    // De slug als die er is: daarmee vindt de bestelbon het artikel (en dus de inkoopprijs) terug,
    // precies zoals bij een webshopbestelling. Een vrij getypte regel houdt `regel-N` — daar is
    // niets te bestellen, dus een streepje op de bon is daar het juiste antwoord.
    productId: r.slug || `regel-${i + 1}`,
    productName: r.omschrijving.trim(),
    type: "article",
    quantity: r.aantal,
    isCustom: false,
    unitPrice: r.stukprijsExclCents,
    linePrice: regelTotaalExcl(r),
    // Het btw-tarief per regel zichtbaar maken zodra er meer dan één tarief op de factuur staat;
    // anders is niet te zien welk bedrag onder welk tarief valt.
    ...(tot.perTarief.length > 1 && !f.btwVerlegd ? { note: `btw ${r.btwTarief}%` } : {}),
    // renderInvoiceHtml zet dit onder de omschrijving als "Artikelnr. …" — dezelfde plek als bij
    // een bestelling, dus losse facturen en webshopfacturen lezen hetzelfde.
    ...(r.artikelnummer ? { articleNumber: r.artikelnummer } : {}),
  }));
  return {
    id: f.id,
    items,
    isCustom: false,
    customer: f.klant,
    totals: { subtotal: tot.subtotaal, shipping: 0, vat: tot.btw, total: tot.totaal },
    ...(f.btwVerlegd
      ? {
          btw: {
            tarief: 0,
            verlegd: true,
            status: "verlegd" as const,
            reden: "Intracommunautaire levering, btw verlegd naar de afnemer.",
            ...(f.klant?.vatNumber ? { vatNumber: f.klant.vatNumber } : {}),
          },
        }
      : { btw: { tarief: VAT_RATE, verlegd: false, status: "nl-tarief" as const, reden: "Nederlandse btw." } }),
    // De betaalstatus volgt `betaaldOp` en niets anders. Stond hier ooit hard "completed", waardoor
    // élke montagefactuur het groene "Voldaan … geldt als betaalbewijs"-blok droeg, ook een die nog
    // betaald moest worden — een onjuiste verklaring op een boekstuk (Taco, 27-10-2026).
    status: f.betaaldOp ? "completed" : "awaiting_payment",
    createdAt: f.datum,
  } as unknown as Order;
}

/**
 * De factuur-HTML van een losse factuur. **Dit is de enige plek die bepaalt hóé een losse factuur
 * getekend wordt** — de voorvertoning in de admin en de mail naar de klant roepen allebei deze
 * functie aan.
 *
 * Waarom dat nodig bleek: beide plekken gaven zélf de opties mee, en de voorvertoning vergat
 * `logoUrl`. Daardoor stond er in het scherm geen logo en in de verstuurde factuur wel — precies
 * het verschil dat deze opzet moest uitsluiten (Thijs, 25-10-2026). Eén functie, één uitkomst.
 *
 * Een concept heeft nog geen nummer en is dus geen boekstuk. Het wordt getekend als
 * "Conceptfactuur" zónder nummerregel en zónder betaalinstructie — eerder stond er "Factuur,
 * factuurnummer CONCEPT" met een overmaakverzoek o.v.v. datzelfde woord.
 */
export function renderFactuurHtml(f: Factuur, opts: Pick<InvoiceRenderOptions, "logoUrl" | "t" | "locale"> = {}): string {
  return renderInvoiceHtml(factuurAlsOrder(f), {
    ...opts,
    ...(f.nummer ? { invoiceNo: f.nummer } : { concept: true }),
    invoiceDate: f.datum,
  });
}
